import { useTranslation } from "react-i18next";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { generateUUID } from "@/utils/transform";

// Color palette for status
const STATUS_COLORS = {
  draft: "#94a3b8", // slate-400
  sent: "#3b82f6", // blue-500
  viewed: "#8b5cf6", // violet-500
  partially_signed: "#f59e0b", // amber-500
  completed: "#22c55e", // green-500
  declined: "#ef4444", // red-500
  voided: "#6b7280", // gray-500
  expired: "#f97316", // orange-500
};

// Custom tooltip for pie chart
function CustomPieTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="bg-popover border rounded-lg shadow-lg px-3 py-2">
        <p className="font-medium">{data.name}</p>
        <p className="text-sm text-muted-foreground">
          {data.value} envelope{data.value !== 1 ? "s" : ""} (
          {data.payload.percentage}%)
        </p>
      </div>
    );
  }
  return null;
}

// Custom tooltip for bar chart
function CustomBarTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border rounded-lg shadow-lg px-3 py-2">
        <p className="font-medium">{label}</p>
        {payload.map((entry) => (
          <p
            key={generateUUID()}
            className="text-sm"
            style={{ color: entry.color }}
          >
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
}

/**
 * Envelope Status Pie Chart
 */
export function StatusPieChart({ stats }) {
  const { t } = useTranslation();

  // Transform stats into chart data
  const data = [
    {
      name: t("envelopes.status.draft"),
      value: stats?.draft || 0,
      status: "draft",
    },
    {
      name: t("envelopes.status.sent"),
      value: stats?.sent || 0,
      status: "sent",
    },
    {
      name: t("envelopes.status.viewed"),
      value: stats?.viewed || 0,
      status: "viewed",
    },
    {
      name: t("envelopes.status.partiallySigned"),
      value: stats?.partiallySigned || 0,
      status: "partially_signed",
    },
    {
      name: t("envelopes.status.completed"),
      value: stats?.completed || 0,
      status: "completed",
    },
    {
      name: t("envelopes.status.declined"),
      value: stats?.declined || 0,
      status: "declined",
    },
    {
      name: t("envelopes.status.voided"),
      value: stats?.voided || 0,
      status: "voided",
    },
    {
      name: t("envelopes.status.expired"),
      value: stats?.expired || 0,
      status: "expired",
    },
  ].filter((item) => item.value > 0); // Only show statuses with values

  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Add percentage to each item
  const dataWithPercentage = data.map((item) => ({
    ...item,
    percentage: total > 0 ? Math.round((item.value / total) * 100) : 0,
  }));

  if (total === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">
            {t("dashboard.charts.statusBreakdown", "Envelope Status")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-50 flex items-center justify-center text-muted-foreground">
            {t("dashboard.charts.noData", "No envelope data yet")}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">
          {t("dashboard.charts.statusBreakdown", "Envelope Status")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-50">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={dataWithPercentage}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {dataWithPercentage.map((entry) => (
                  <Cell
                    key={generateUUID()}
                    fill={STATUS_COLORS[entry.status] || "#94a3b8"}
                    className="stroke-background"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                iconType="circle"
                iconSize={8}
                formatter={(value) => (
                  <span className="text-sm text-foreground">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Monthly Activity Bar Chart
 */
export function ActivityBarChart({ monthlyData }) {
  const { t } = useTranslation();

  // Generate last 6 months of data
  const getMonthlyData = () => {
    const months = [];
    const now = new Date();

    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      const monthName = date.toLocaleDateString("en-US", { month: "short" });

      const monthStats = monthlyData?.[monthKey] || {};

      months.push({
        month: monthName,
        sent: monthStats.sent || 0,
        completed: monthStats.completed || 0,
      });
    }

    return months;
  };

  const data = getMonthlyData();
  const hasData = data.some((d) => d.sent > 0 || d.completed > 0);

  if (!hasData) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">
            {t("dashboard.charts.monthlyActivity", "Monthly Activity")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-50 flex items-center justify-center text-muted-foreground">
            {t("dashboard.charts.noActivity", "No activity data yet")}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">
          {t("dashboard.charts.monthlyActivity", "Monthly Activity")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-50">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barGap={4}>
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip
                content={<CustomBarTooltip />}
                cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }}
              />
              <Legend
                iconType="circle"
                iconSize={8}
                formatter={(value) => (
                  <span className="text-sm text-foreground">{value}</span>
                )}
              />
              <Bar
                dataKey="sent"
                name={t("dashboard.charts.sent", "Sent")}
                fill="#3b82f6"
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
              <Bar
                dataKey="completed"
                name={t("dashboard.charts.completed", "Completed")}
                fill="#22c55e"
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Combined Dashboard Charts Section
 */
export default function DashboardCharts({ stats, monthlyData }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <StatusPieChart stats={stats} />
      <ActivityBarChart monthlyData={monthlyData} />
    </div>
  );
}
